import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Icon } from "@/components/Icon";
import { Reveal } from "@/components/Reveal";
import { Logo } from "@/components/Logo";
import { useSite, mediaUrl, whatsappHref } from "@/context/SiteContext";
import { useHiringCounty } from "@/lib/useHiringCounty";
import { IMAGES, COMPANY } from "@/lib/data";
import { WhatsappLogo, MapPin, ArrowRight, NotePencil, CheckCircle, EnvelopeSimple, Lightning } from "@phosphor-icons/react";

const CATS = [
  { key: "Transportation", label: "Transportation / NEMT", eyebrow: "Drive with us", icon: "Ambulance", accent: "text-brand-teal", ring: "border-brand-teal/20", bg: "from-brand-teal/[0.06]" },
  { key: "ODP", label: "ODP / Human Services", eyebrow: "Support independence", icon: "HandHeart", accent: "text-brand-purple", ring: "border-brand-purple/20", bg: "from-brand-purple/[0.06]" },
];

function JobCard({ job }) {
  const counties = (job.counties || []);
  const area = counties.includes("Statewide") ? "Statewide (PA)" : counties.length >= 10 ? "All service-area counties" : counties.length ? `${counties.length} counties` : "Pennsylvania";
  return (
    <Reveal>
      <Link to={`/careers/${job.id}`} data-testid={`job-card-${job.id}`}
        className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-brand-purple/30 hover:shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading text-lg font-bold text-slate-900">{job.title}</h3>
          {job.urgent && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-bold text-rose-600">
              <Lightning size={12} weight="fill" /> Urgently Hiring
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {(job.employment_types || []).map((t) => (
            <span key={t} className="rounded-full bg-brand-bg px-2.5 py-1 text-xs font-semibold text-slate-600">{t}</span>
          ))}
        </div>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600 line-clamp-3">{job.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500"><MapPin size={14} weight="fill" className="text-brand-teal" /> {area}</span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-purple">View &amp; Apply <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-1" /></span>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Careers() {
  const content = useSite();
  const countyParam = new URLSearchParams(window.location.search).get("county") || "";
  const { county } = useHiringCounty(countyParam);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/job-openings").then((r) => setJobs(r.data)).catch(() => {});
    try {
      if (sessionStorage.getItem("cd_ticker_click")) {
        sessionStorage.removeItem("cd_ticker_click");
        api.post("/track/ticker-proceed").catch(() => {});
      }
    } catch (e) { /* ignore */ }
  }, []);

  const heroImg = content.careers_hero_image_id ? mediaUrl(content.careers_hero_image_id) : IMAGES.recruitment;
  const waOn = content.whatsapp_enabled !== false && (content.whatsapp_number || "").trim();

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-blue noise-overlay" data-testid="careers-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:px-8 py-14 lg:grid-cols-2 lg:gap-10 lg:py-20">
          <Reveal>
            <Logo variant="light" className="h-10 sm:h-11" />
            {county ? (
              <p className="eyebrow mt-6 inline-flex items-center gap-2 rounded-full bg-brand-purple/90 px-4 py-2 text-white" data-testid="careers-county-eyebrow">
                <MapPin size={16} weight="fill" className="text-brand-teal2" /> Now Hiring — {county} County
              </p>
            ) : (
              <p className="eyebrow mt-6 text-brand-teal2">Careers Across Our Pennsylvania Service Area</p>
            )}
            <h1 className="mt-4 font-heading text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Join the CareDeserved Team
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-200 md:text-lg">
              We're hiring compassionate Transportation and ODP / Human Services professionals — Full-Time &amp; Part-Time.
              Explore our open positions below and apply today.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href="#positions" data-testid="hero-view-openings"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-brand-purple px-8 text-base font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-brand-purple2">
                <NotePencil size={20} weight="fill" /> View Job Openings <ArrowRight size={18} weight="bold" />
              </a>
              {waOn && (
                <a href={whatsappHref(content)} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-full border-2 border-white/40 bg-white/5 px-6 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/15">
                  <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" /> Questions? WhatsApp Us
                </a>
              )}
            </div>
            <p className="mt-4 text-sm text-slate-300" data-testid="careers-email">
              Prefer email? Reach our recruitment team at{" "}
              <a href={`mailto:${COMPANY.careersEmail}`} className="inline-flex items-center gap-1 font-semibold text-brand-teal2 underline underline-offset-2 hover:text-white">
                <EnvelopeSimple size={15} weight="fill" /> {COMPANY.careersEmail}
              </a>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/20">
              <img src={heroImg} alt="A uniformed CareDeserved team member beside a company transportation van"
                className="h-[300px] w-full object-cover sm:h-[400px] lg:h-[460px]" loading="eager" decoding="async" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* OPEN POSITIONS */}
      <section id="positions" className="scroll-mt-24 bg-white py-16 sm:py-24" data-testid="careers-positions">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="eyebrow text-brand-purple">Open Positions</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Explore Careers at CareDeserved</h2>
            <p className="mt-3 text-slate-600">Two ways to make a difference — behind the wheel or in the community.</p>
          </div>

          {CATS.map((cat) => {
            const list = jobs.filter((j) => j.category === cat.key);
            if (list.length === 0) return null;
            return (
              <div key={cat.key} className="mt-14" data-testid={`career-cat-${cat.key.toLowerCase()}`}>
                <div className="flex items-center gap-3">
                  <span className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${cat.bg} to-transparent ${cat.accent} ring-1 ${cat.ring}`}>
                    <Icon name={cat.icon} size={26} weight="fill" />
                  </span>
                  <div>
                    <p className={`eyebrow ${cat.accent}`}>{cat.eyebrow}</p>
                    <h3 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">{cat.label}</h3>
                  </div>
                </div>
                <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((job) => <JobCard key={job.id} job={job} />)}
                </div>
              </div>
            );
          })}

          {jobs.length === 0 && (
            <p className="mt-12 text-center text-slate-400">No open positions right now — please check back soon.</p>
          )}
        </div>
      </section>
    </>
  );
}
